import { BaseResource } from './base';
import {
  Tag,
  CreateTagDto,
  UpdateTagDto,
  AssignTagsDto,
  SearchTagsParams,
  GetTagClientsParams,
  AssignTagsResponse,
  RemoveTagsResponse,
  DeleteTagResponse,
  ClientTagsResponse,
  TagClientsResponse,
  TagStatsResponse,
} from '../models/tags';
import { RequestOptions, PaginatedResponseDto } from '../types';

export class TagsResource extends BaseResource {
  private basePath = 'tags';

  // ==================== Tag Management ====================

  /**
   * Create a new tag
   * POST /api/v1/tags
   */
  async createTag(data: CreateTagDto, options?: RequestOptions): Promise<Tag> {
    return this.client.post<Tag>(this.basePath, data, options);
  }

  /**
   * List all tags with optional filters and pagination
   * GET /api/v1/tags
   */
  async searchTags(
    params?: SearchTagsParams,
    options?: RequestOptions,
  ): Promise<PaginatedResponseDto<Tag>> {
    return this.client.get<PaginatedResponseDto<Tag>>(this.basePath, params, options);
  }

  /**
   * Get a specific tag by ID
   * GET /api/v1/tags/:id
   */
  async getTag(id: string, options?: RequestOptions): Promise<Tag> {
    return this.client.get<Tag>(`${this.basePath}/${id}`, undefined, options);
  }

  /**
   * Update an existing tag
   * PUT /api/v1/tags/:id
   */
  async updateTag(id: string, data: UpdateTagDto, options?: RequestOptions): Promise<Tag> {
    return this.client.put<Tag>(`${this.basePath}/${id}`, data, options);
  }

  /**
   * Delete a tag (soft delete)
   * DELETE /api/v1/tags/:id
   * @param id - Tag ID
   * @param force - Force deletion even if tag has assigned clients
   */
  async deleteTag(
    id: string,
    force?: boolean,
    options?: RequestOptions,
  ): Promise<DeleteTagResponse> {
    const path = force ? `${this.basePath}/${id}?force=true` : `${this.basePath}/${id}`;
    return this.client.delete<DeleteTagResponse>(path, options);
  }

  /**
   * Get clients with a specific tag
   * GET /api/v1/tags/:id/clients
   */
  async getTagClients(
    id: string,
    params?: GetTagClientsParams,
    options?: RequestOptions,
  ): Promise<TagClientsResponse> {
    return this.client.get<TagClientsResponse>(`${this.basePath}/${id}/clients`, params, options);
  }

  /**
   * Get tag statistics
   * GET /api/v1/tags/stats
   */
  async getTagStats(options?: RequestOptions): Promise<TagStatsResponse> {
    return this.client.get<TagStatsResponse>(`${this.basePath}/stats`, undefined, options);
  }

  // ==================== Client Tag Assignment ====================

  /**
   * Assign one or multiple tags to a client
   * POST /api/v1/clients/:clientId/tags
   */
  async assignTagsToClient(
    clientId: string,
    data: AssignTagsDto,
    options?: RequestOptions,
  ): Promise<AssignTagsResponse> {
    return this.client.post<AssignTagsResponse>(`clients/${clientId}/tags`, data, options);
  }

  /**
   * Remove a specific tag from a client
   * DELETE /api/v1/clients/:clientId/tags/:tagId
   */
  async removeTagFromClient(
    clientId: string,
    tagId: string,
    options?: RequestOptions,
  ): Promise<RemoveTagsResponse> {
    return this.client.delete<RemoveTagsResponse>(`clients/${clientId}/tags/${tagId}`, options);
  }

  /**
   * Remove multiple tags from a client
   * DELETE /api/v1/clients/:clientId/tags
   * Note: This endpoint requires sending tagIds in the request body
   */
  async removeTagsFromClient(
    clientId: string,
    tagIds: string[],
    options?: RequestOptions,
  ): Promise<RemoveTagsResponse> {
    // DELETE with body requires special handling
    return this.request<RemoveTagsResponse>(`clients/${clientId}/tags`, {
      method: 'DELETE',
      body: JSON.stringify({ tagIds }),
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
  }

  /**
   * Get all tags assigned to a client
   * GET /api/v1/clients/:clientId/tags
   */
  async getClientTags(clientId: string, options?: RequestOptions): Promise<ClientTagsResponse> {
    return this.client.get<ClientTagsResponse>(`clients/${clientId}/tags`, undefined, options);
  }
}
